import type { CommonArea, CommonAreaBooking } from '../../services/commonAreaReservationService';
import { bookingCalendarDate, wallClock } from '../../pages/bookingWhen';
import { bookingFullDayLabel } from './bookingLabels';

/**
 * How a condomínio's reservation list is ordered and searched.
 *
 * "Lista de Reservas" arrived in whatever order the API returned and had no search at all, so
 * finding "the Churrasqueira booking Claudia made for the 25th" among a few hundred rows meant
 * scrolling. Both decisions live here rather than in the table: they are rules, they are asserted
 * (bookingListView.test.ts), and the operational panel reads the same list the same way.
 */

export type BookingSortKey = 'area' | 'person' | 'date' | 'hours' | 'purpose' | 'status';
export type SortDirection = 'asc' | 'desc';

/** What the status column means, ordered by what still needs an answer. */
const STATUS_RANK: Record<string, number> = {
  Pending: 0,
  Confirmed: 1,
  Approved: 2,
  Cancelled: 3,
  Revoked: 4,
};

/** The status as the row shows it — searching "revogado" has to find the Revoked rows. */
const STATUS_TEXT: Record<string, string> = {
  Pending: 'Pendente',
  Confirmed: 'Confirmado',
  Approved: 'Aprovado',
  Cancelled: 'Cancelado',
  Revoked: 'Revogado',
};

/**
 * Compared the way a person reads: accents folded and case ignored, so "salao" finds "Salão" and
 * "André" sorts with the A's instead of after Z.
 */
const fold = (value: string): string =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const areaNameOf = (b: CommonAreaBooking, areas: CommonArea[]): string =>
  areas.find(a => a.id === b.common_area_id)?.name ?? '';

const personNameOf = (b: CommonAreaBooking): string => {
  const p = b.person;
  return [p?.first_name, p?.last_name].filter(Boolean).join(' ').trim();
};

/** The value a column sorts on. Empty means "nothing to sort on" — those rows go last. */
function sortValue(b: CommonAreaBooking, key: BookingSortKey, areas: CommonArea[]): string {
  switch (key) {
    case 'area': return fold(areaNameOf(b, areas));
    case 'person': return fold(personNameOf(b));
    // The booking's own calendar date, never `new Date(iso)`: that is what put a reservation for
    // the 25th on the wrong side of one made for the 24th.
    case 'date': return bookingCalendarDate(b.start_time) ?? '';
    case 'hours': return wallClock(b.start_time) ?? '';
    case 'purpose': return fold(b.purpose || '');
    case 'status': return String(STATUS_RANK[b.status] ?? 9);
  }
}

/**
 * The list in the asked-for order. Never mutates the input — React state is handed to this
 * directly, and an in-place sort there is a render that does not happen.
 *
 * A row with nothing in the sorted column stays at the BOTTOM whichever way the column points:
 * an empty cell winning first place in ascending order is noise at the top of the list.
 */
export function sortBookings(
  bookings: CommonAreaBooking[],
  key: BookingSortKey | null,
  direction: SortDirection,
  areas: CommonArea[],
): CommonAreaBooking[] {
  if (!key) return bookings;
  const factor = direction === 'asc' ? 1 : -1;
  return [...bookings].sort((a, b) => {
    const left = sortValue(a, key, areas);
    const right = sortValue(b, key, areas);
    if (!left && !right) return 0;
    if (!left) return 1;
    if (!right) return -1;
    return left.localeCompare(right, 'pt-BR') * factor;
  });
}

/** Clicking the same header flips it; a different header starts ascending. */
export function nextSortDirection(
  clicked: BookingSortKey,
  current: BookingSortKey | null,
  direction: SortDirection,
): SortDirection {
  return clicked === current && direction === 'asc' ? 'desc' : clicked === current ? 'asc' : 'asc';
}

/**
 * Everything one row can be found by: the área, the morador, the unit (how the portaria knows a
 * resident), the finalidade, the status as it is written on screen, and the date as it is written
 * in the row — "31/10" has to find the row that shows 31/10/2026.
 */
function haystack(b: CommonAreaBooking, areas: CommonArea[]): string {
  return fold([
    areaNameOf(b, areas),
    personNameOf(b),
    b.unit_label || '',
    b.purpose || '',
    STATUS_TEXT[b.status] || b.status,
    bookingFullDayLabel(b),
  ].join(' '));
}

export function searchBookings(
  bookings: CommonAreaBooking[],
  query: string,
  areas: CommonArea[],
): CommonAreaBooking[] {
  const needle = fold(query);
  if (!needle) return bookings;
  return bookings.filter(b => haystack(b, areas).includes(needle));
}
